import { Search } from "lucide-react";
import type { ComponentProps } from "react";

import { ClearButton, controlClass, controlHeight } from "@/components/ui/field";
import { cn } from "@/utils/utils";

/**
 * The one search box.
 *
 * `type="search"` for the platform's own clear affordance and history, plus an
 * explicit clear button — the native one is invisible in several browsers, and
 * "how do I get all the rows back" should never be a question.
 *
 * Nothing the browser remembers is offered here. A clerk's Chrome has a pile of
 * saved addresses under their own Google account — members' names among them,
 * which is the thing `MemberNewPage`'s `autoComplete="off"` exists to stop being
 * added to — and it was offering them back over this box: a floating list of
 * strangers' names, drawn by the browser on top of the app's own search history
 * panel and covering it. The two lists are not even answering the same question.
 * This one is: the history under this box is the account's own, read from the
 * server, and it is the only list that should appear under it.
 *
 * Which is why `autocomplete` here reads `new-password` on a search box.
 *
 * `off` is the honest value and it does not work. Chrome has ignored it for
 * address fields for a decade — deliberately, because sites were using it to
 * switch autofill off wholesale — and this box is read as one: it is not inside
 * a form, so Chrome gathers every loose field on the page into a synthetic one,
 * classifies the group as an address form and hands the text box at the top of
 * it a name. `off` is then advice Chrome has already decided to disregard.
 *
 * What it does not disregard is an explicit type. A field the page has declared
 * a password is never offered an address, and on `type="search"` the declaration
 * costs nothing else: password generation and the save prompt are `type="password"`
 * only, so nothing is drawn here at all. `one-time-code` works the same way; this
 * one is the better known of the two, which matters more than the wording when
 * the next person reads it.
 *
 * Should Chrome ever start honouring `off` here, this goes back to `off`.
 */
export function SearchInput({
  className,
  containerClassName,
  onClear,
  value,
  fieldSize = "md",
  ...props
}: ComponentProps<"input"> & {
  containerClassName?: string;
  onClear?: () => void;
  fieldSize?: keyof typeof controlHeight;
}) {
  const filled = value !== undefined && value !== "";
  // Clearing is a search — it asks for every row back. So it is held whenever
  // the box is, and `readOnly` on an input does not reach a sibling button.
  const held = Boolean(props.readOnly || props.disabled);
  return (
    <div className={cn("relative min-w-0", containerClassName)}>
      <Search
        aria-hidden
        className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <input
        type="search"
        value={value}
        // Before the spread, so a caller that genuinely wants a browser
        // suggestion can still ask for one. See the note above for why a search
        // box declares itself a password field.
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        // The password managers keep their own lists and their own overlay, and
        // they read a lone text field as a username often enough to draw it.
        // Each vendor has its own opt-out and they all cost nothing here.
        data-1p-ignore=""
        data-lpignore="true"
        data-form-type="other"
        className={cn(
          controlClass,
          controlHeight[fieldSize],
          "pl-9.5",
          filled && onClear ? "pr-9" : "",
          "[&::-webkit-search-cancel-button]:hidden",
          className,
        )}
        {...props}
      />
      {filled && onClear ? (
        // A real button here: it stands beside the box rather than inside its
        // trigger, and clearing a search is worth reaching by keyboard.
        <ClearButton
          focusable
          label="Clear search"
          onClear={onClear}
          disabled={held}
          className="absolute top-1/2 right-2 size-6 -translate-y-1/2"
        />
      ) : null}
    </div>
  );
}
